import { useIntl } from 'react-intl';

import ElasticPaginatedSelect from '@src/components/ElasticPaginatedSelect';
import { FSCollections } from '@src/constants/database';
import type { UserListSearchField } from '@src/models/mappers/UserMapper';

import { buildElasticUserSelectOptions } from './buildElasticUserSelectOptions';
import type { ElasticUserSelectProps, ElasticUserSource } from './ElasticUserSelect.interfaces';

const DEFAULT_USER_SEARCH_FIELDS: UserListSearchField[] = [
  'firstName',
  'lastName',
  'email',
  'idCard',
];

/**
 * User picker backed by Elasticsearch. Options show the name with the email as
 * secondary text; the selected state keeps only the name.
 */
const ElasticUserSelect = ({
  searchFields = DEFAULT_USER_SEARCH_FIELDS,
  pageSize = 20,
  listHeight = 256,
  mode,
  placeholder,
  emptyPlaceholder = 'elasticUserSelect.empty',
  ariaLabel,
  maxTagCount,
  maxTagTextLength,
  ...props
}: ElasticUserSelectProps) => {
  const intl = useIntl();

  return (
    <ElasticPaginatedSelect<ElasticUserSource>
      {...props}
      entity={FSCollections.USERS}
      searchKey={searchFields}
      parseData={buildElasticUserSelectOptions}
      optionLabelProp="selectionLabel"
      pageSize={pageSize}
      listHeight={listHeight}
      mode={mode}
      placeholder={placeholder ?? intl.formatMessage({ id: 'elasticUserSelect.search.placeholder' })}
      emptyPlaceholder={emptyPlaceholder}
      ariaLabel={ariaLabel ?? intl.formatMessage({ id: 'elasticUserSelect.search.accessibleLabel' })}
      maxTagCount={mode === 'multiple' ? (maxTagCount ?? 'responsive') : maxTagCount}
      maxTagTextLength={mode === 'multiple' ? (maxTagTextLength ?? 40) : maxTagTextLength}
    />
  );
};

export default ElasticUserSelect;
